import { SIDO_SHORT_BY_NAME } from '@/lib/cl-core/names';
import { SIDO_NAMES } from '@/lib/regions';
/**
 * search_unify1 #4g.1 — 현장 행 보조줄 한 벌(「시도 시군구 · 단계」). /search 결과 행 · 자동완성 행 · 인기 단지 행이 같이 쓴다.
 *   · 지역은 1회: 낱말(공백 단위) 중복 제거 — 「부산 금정구 부산 금정구」 · region 「부산 금정구」 + sigungu 「금정구」 → 「부산 금정구」.
 *   · 조각 중복 제거: 공백 정규화 비교(앞 조각과 같은 조각 · 앞 조각에 통째로 든 조각은 버림).
 *   · 값 있는 것만(수치 지어내기 0). 순수 함수(테스트 대상).
 */

const sp = (s: unknown) => String(s ?? '').replace(/\s+/g, ' ').trim();
const nsp = (s: string) => s.replace(/\s+/g, '');

/** 지역 낱말 이어 붙이기 — 같은 낱말은 한 번만(순서 유지). */
export function placeText(...parts: Array<string | null | undefined>): string {
  const out: string[] = [];
  for (const p of parts) for (const t of sp(p).split(' ')) if (t && !out.includes(t)) out.push(t);
  return out.join(' ');
}

/** 조각 묶기 — 첫 조각(지역)은 낱말 중복 제거 · 같은/포함된 조각 버림 · 「 · 」 로 잇기. */
export function subline(pieces: Array<string | null | undefined>, sep = ' · '): string {
  const out: string[] = [];
  for (const raw of pieces) {
    const v = out.length === 0 ? placeText(raw) : sp(raw);
    if (!v) continue;
    const k = nsp(v);
    if (out.some((o) => nsp(o) === k || nsp(o).includes(k))) continue;
    out.push(v);
  }
  return out.join(sep);
}

/** 「부산 금정구 · 사업시행인가」 */
export function siteSubline(x: { region?: string | null; sigungu?: string | null; stageLabel?: string | null; extra?: Array<string | null | undefined> }): string {
  return subline([placeText(x.region, x.sigungu), x.stageLabel ?? '', ...(x.extra ?? [])]);
}

/* ─── #4g.3 — 목록 표시 이름: 단지명 앞 「시도 시군구」 접두 제거(보조줄에 지역이 있으므로) ───────────────
 * 규칙(목록 표시만 — 상세 h1·title·rank_lock 불변):
 *   · 그 현장 자신의 시도 + 그 현장 자신의 시군구가 «연달아» 맨 앞일 때만 둘 다 뗀다(「부산 연제구 더샵…」 → 「더샵…」).
 *   · 시도 단독(「부산 에코델타…」 — 브랜드·이름 본체) · 시군구 단독(「부산진구 시민공원…」) · 다른 시도 = 원문.
 *   · region 을 모르는 행(인기 단지)은 시도 낱말 + 그 현장 시군구가 이어질 때만.
 *   · 뗀 뒤 빈 문자열·2글자 미만이면 원문.
 */
const SIDO_LONG: Readonly<Record<string, string>> = SIDO_SHORT_BY_NAME; // 정본 cl-core/names(HC 정리 — 사본 금지)
const SIDO_SHORT_SET = new Set(SIDO_NAMES);

/** 시도 낱말 → 짧은 이름(시도가 아니면 null). */
export function sidoKey(token: string | null | undefined): string | null {
  const t = sp(token);
  if (!t) return null;
  if (SIDO_SHORT_SET.has(t)) return t;
  return SIDO_LONG[t] ?? null;
}

export function displaySiteName(name: string | null | undefined, site: { region?: string | null; sigungu?: string | null } = {}): string {
  const raw = sp(name);
  if (!raw) return raw;
  const toks = raw.split(' ');
  const sido = sidoKey(toks[0]);
  if (!sido) return raw; // 시도로 시작하지 않으면 그대로(시군구명이 이름 본체인 단지 보호)
  const own = sidoKey(placeText(site.region).split(' ')[0] ?? '');
  const sgg = placeText(site.sigungu).split(' ').filter((t) => t && !sidoKey(t));
  // 그 현장 자신의 시도 + 그 현장 자신의 시군구가 «연달아» 맨 앞일 때만 둘 다 뗀다. 시도 단독(「부산 에코델타…」)·시군구 단독·다른 시도 = 원문.
  if (own && own !== sido) return raw;
  if (!sgg.length || !sgg.every((t, k) => toks[1 + k] === t)) return raw;
  const i = 1 + sgg.length;
  const rest = toks.slice(i).join(' ').replace(/^[—–\-·:|]\s*/, '');
  return [...rest].length >= 2 ? rest : raw;
}

/** 「부산 금정구 · 사업시행인가」·「부산 금정구」 같은 보조줄/부제 → { region, sigungu }(첫 낱말이 시도일 때만). */
export function placeOf(subtitle: string | null | undefined): { region: string | null; sigungu: string | null } {
  const place = sp(String(subtitle ?? '').split(' · ')[0]);
  const toks = place.split(' ').filter(Boolean);
  const sido = sidoKey(toks[0]);
  if (!sido) return { region: null, sigungu: null };
  return { region: sido, sigungu: toks.slice(1).join(' ') || null };
}
